import { computed } from 'vue';
import useStore from '~/composables/useStore';
import { i18n } from '~/plugins/i18n';
import Chip from '~/entities/chip';
import usePersistedPreferences from '~/composables/usePersistedPreferences';

export default function useEventType() {
  /**
 * Use store
 */
  const store = useStore();

  /**
   * All event types in a flat structure
   */
  const flatEventTypes = computed(() => store.getters['events/getFlatEventTypes']);

  /**
   * Fetch the event types from API (in a flat way)
   */
  function fetchFlatEventTypes() {
    return store.dispatch('events/fetchFlatEventTypes');
  }

  function getEventType(eventTypeId) {
    return store.getters['events/getEventType'](eventTypeId);
  }

  /**
    * Returns the whole event type object from its id
    */
  function getEventTypeWithTrashed(eventTypeId) {
    return store.getters['events/getFlatEventTypeWithTrashed'](eventTypeId);
  }

  /**
   * Returns the friendly name of an event type
   */
  function getEventTypeName(eventTypeId) {
    return store.getters['events/getEventTypeName'](eventTypeId, i18n.global.locale.value);
  }

  /**
   * Returns the friendly name of an event type even trashed Event
   */
  function getEventTypeWithTrashedName(eventTypeId) {
    return store.getters['events/getFlatEventTypeWithTrashedName'](eventTypeId, i18n.global.locale.value);
  }

  /**
   * Returns the whole event type object from its code
   */
  function getEventTypeByCode(eventTypeCode) {
    const flatTypes = store.getters['events/getFlatEventTypes'];

    return flatTypes.find(
      (et) => et.code
        && et.code.toLowerCase() === eventTypeCode.toLowerCase(),
    );
  }

  /**
   * Returns true if the event type should be displayed (in event type picker)
   */
  function isShowed(eventType) {
    return eventType.active;
  }

  /**
   * Returns the ids of the event types that act as categories
   * @param allEventTypes
   * @param expand
   */
  function getBaseCategories(allEventTypes = [], expand = false) {
    return allEventTypes.filter(
      (eventType) => eventType.category && (!eventType.parent || expand),
    );
  }

  function compareBaseCategory(categoryA, categoryB) {
    // make category with id 23  at last position
    if (categoryA.id === '1') return 1;
    if (categoryB.id === '1') return -1;
    return 0;
  }

  function sortBaseCategories(baseCategory, expand) {
    //  .reverse() // to have Unplanned stop first
    if (!expand) {
      baseCategory.reverse();
    }
    return (expand) ? baseCategory.sort(compareBaseCategory) : baseCategory;
  }

  /**
   * Compare two event types based on their names
   * @param eventTypeA
   * @param eventTypeB
   * @returns {*}
   */
  function compareEventTypeAlphabetically(eventTypeA, eventTypeB) {
    const eventTypeAName = `${eventTypeA.name}`;
    const eventTypeBName = `${eventTypeB.name}`;

    return eventTypeAName.localeCompare(eventTypeBName);
  }

  /**
   * Returns all the event types under the provided base event types
   * @param baseEventTypeIds
   * @param allEventTypes
   * @param recursive
   */
  function getEventTypes(baseEventTypeIds = [], allEventTypes = [], recursive = false) {
    if (allEventTypes.length > 0) {
      let result = [];
      const max = baseEventTypeIds.length;
      for (let i = 0; i < max; i += 1) {
        const eventTypes = allEventTypes.filter(
          (eventType) => eventType.parent === baseEventTypeIds[i],
        );

        if (eventTypes.length > 0) {
          if (recursive) {
            for (let j = 0; j < eventTypes.length; j += 1) {
              result.push(eventTypes[j]);
              // Trying to retrieve the event types under the ones I just found
              // and adding them just after their parent
              result = result.concat(...getEventTypes(
                [eventTypes[j].id],
                allEventTypes,
                false,
              ));
            }
          } else {
            result = result.concat(...eventTypes);
          }
        }
      }

      return result;
    }

    return [];
  }

  // Persisted "event type picker expanded" preference, shared by every picker
  const { eventTypePickerExpanded: eventTypePickerExpand } = usePersistedPreferences();

  // Retun if teh event list is loaded
  const isEventListLoaded = () => store.state.events.flatEventTypes.length > 0;

  /**
   * Returns event types in the format of Chips, each one wearing its own colour when the query
   * asked for it: not every fragment fetches "color"
   * @param eventTypes Provide an array of event types or a single one
   * @param defaultColor Colour of the event types left uncoloured, none by default
   */
  function formatEventTypeChipList(eventTypes, defaultColor = undefined) {
    const toChip = (eventType) => new Chip({
      id: eventType.id,
      title: eventType.name,
      color: eventType.color || defaultColor,
    });

    if (!Array.isArray(eventTypes)) return [toChip(eventTypes)];

    return eventTypes.map(toChip);
  }

  return {
    flatEventTypes,
    formatEventTypeChipList,
    eventTypePickerExpand,
    fetchFlatEventTypes,
    getEventType,
    getEventTypeByCode,
    getEventTypeWithTrashed,
    getEventTypeName,
    getEventTypeWithTrashedName,
    isShowed,
    getBaseCategories,
    compareBaseCategory,
    sortBaseCategories,
    compareEventTypeAlphabetically,
    getEventTypes,
    isEventListLoaded,
  };
}
